import { Component, effect, EventEmitter, Input, Output } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Assembly, AssemblyPriceTable, AssemblySaveRequest } from 'src/app/models/assembly.model';
import { AssemblyPriceListComponent } from '../assembly-price-list/assembly-price-list.component';
import { AssemblyPricesService } from './assembly-prices.service';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RichTextEditorComponent } from '@/global/rich-text-editor.component';
import { CategoriesService } from '@/services/api/categories.service';
import { AssembliesService } from '@/services/api/assemblies.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { OfficesService } from '@/services/api/franchises.service';
import { BannerMessage, BannerMessagesService } from '@/services/app/banner-messages.service';
import { AssemblyCacheService } from '@/services/app/assembly-cache.service';
import { FluidModule } from 'primeng/fluid';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-assembly-editor',
  imports: [
    MessageModule,
    RichTextEditorComponent,
    CommonModule,
    FormsModule,
    AssemblyPriceListComponent,
    FluidModule,
    SelectModule,
    ButtonModule
  ],
  template: `
    <p-message *ngFor="let message of messages" closable class="mb-2">
      {{message.summary}}
    </p-message>

    <p-fluid>
      <div class="flex flex-col md:flex-row gap-4" *ngIf="currentAssembly">
        <div class="md:w-1/2">
          <div class="flex flex-wrap gap-4">
            <div class="flex flex-col grow basis-0 gap-2">
              <label>Category</label>
              <p-select
                [options]="categoriesService.categories()"
                [filter]="true"
                filterBy="Name"
                [(ngModel)]="selectedCategory"
                (onChange)="selectCategory($event.value)"
                optionLabel="Name"
                optionValue="CategoryID"
                placeholder="Select a category">
              </p-select>
            </div>

            <div class="flex flex-col grow basis-0 gap-2">
              <label>Type</label>
              <p-select
                [options]="assemblyTypes"
                filterBy="label"
                [(ngModel)]="selectedAssemblyType"
                (onChange)="selectAssemblyType($event.value)"
                optionLabel="label"
                optionValue="value"
                placeholder="Select a type">
              </p-select>
            </div>
          </div>

          <div class="flex flex-col gap-2 mt-2">
            <label>Description</label>
            <app-rich-text-editor [(ngModel)]="currentAssembly!.Description"></app-rich-text-editor>
          </div>
        </div>

        <div class="md:w-1/2">
          <div class="flex flex-col gap-2">
            <!-- Keep your price list UI as-is; it should expose APID for existing rows -->
            <app-assembly-price-list (onSave)="onSavePriceList($event)"></app-assembly-price-list>
          </div>
        </div>
      </div>

      <div class="flex mt-6">
        <div class="flex flex-row gap-4 w-full justify-content-end">
        <p-button
        severity="secondary"
            label="Cancel"
            styleClass="w-auto "
            [disabled]="loading"
            (click)="onCancel()">
          </p-button>
          <p-button
          severity="success"
            label="Save Changes"
            styleClass="w-auto mr-auto"
            [disabled]="loading"
            (click)="onSave()">
          </p-button>
        </div>
      </div>
    </p-fluid>
  `
})
export class OpsAssemblyEditorComponent {
  @Input() templateMode: boolean = false;
  @Output() save = new EventEmitter<Assembly>();
  @Output() cancel = new EventEmitter<void>();

  selectedCategory: number | null = null;
  loading = false;
  categoriesMenu: MenuItem[] = [];
  currentAssembly: Assembly | null = null;
  selectedAssemblyType: string | null = null;
  addPriceList = false;

  assemblyTypes: { label: string; value: string }[] = [
    { label: 'Standard', value: 'S' },
    { label: 'Option', value: 'O' }
  ];

  constructor(
    public categoriesService: CategoriesService,
    public selectedAssemblyService: AssembliesService,
    public binderDialogService: BinderDialogService,
    public assemblyPricesService: AssemblyPricesService,
    public assembliesService: AssembliesService,
    public officesService: OfficesService,
    public bannerMessagesService: BannerMessagesService,
    public assemblyCacheService: AssemblyCacheService
  ) {
    effect(() => {
      const selectedAssembly = this.selectedAssemblyService.selectedAssembly();
      const selectedCategory = this.categoriesService.selectedCategory();
      const selectedAssemblyType = this.selectedAssemblyService.selectedAssembly()?.Type || null;

      if (selectedAssembly) {
        this.currentAssembly = selectedAssembly;

        // Price service receives the server's current rows (these include APID for existing)
        this.assemblyPricesService.setAssemblyPrices(selectedAssembly.AssemblyPriceTable ?? []);

        // If your price list UI previously tracked "removed" rows,
        // that’s no longer needed for an idempotent payload.
        this.assemblyPricesService.removedPrices.set([]); // no-op; safe to remove later
      }

      if (selectedCategory?.CategoryID !== this.selectedCategory) {
        this.selectedCategory = selectedCategory?.CategoryID ?? null;
      }

      if (selectedAssemblyType !== this.selectedAssemblyType) {
        this.selectedAssemblyType = selectedAssemblyType;
        this.isInherited(selectedAssemblyType ?? '');
      }
    });
  }

  get messages(): BannerMessage[] {
    return this.bannerMessagesService.messages()?.filter(m => m.type === 'assembly') || [];
  }
  set messages(messages: BannerMessage[]) {
    this.bannerMessagesService.replaceMessageType(messages, 'assembly');
  }

  onCancel() {
    this.cancel.emit();
  }

  selectCategory(categoryId: number) {
    this.selectedCategory = categoryId;
    if (this.currentAssembly) this.currentAssembly.CategoryID = categoryId;
  }

  selectAssemblyType(assemblyType: string) {
    this.selectedAssemblyType = assemblyType;
    this.isInherited(assemblyType);
    if (this.currentAssembly) this.currentAssembly.Type = assemblyType;
  }

  isInherited(type: string) {
    const selectedOffice = this.officesService.selectedOffice();
    this.bannerMessagesService.clearType('assembly');

    if (type === 'O') {
      if (selectedOffice?.Options_FID !== null && selectedOffice?.Options_FID !== selectedOffice?.FID) {
        this.bannerMessagesService.replaceMessageType(
          [{ severity: 'warn', type: 'assembly', summary: 'Using Inherited Options', detail: 'This assembly will be saved to the inherited office' }],
          'assembly'
        );
      }
    }

    if (type === 'S') {
      if (selectedOffice?.Standards_FID !== null && selectedOffice?.Standards_FID !== selectedOffice?.FID) {
        this.bannerMessagesService.replaceMessageType(
          [{ severity: 'warn', type: 'assembly', summary: 'Using Inherited Standards', detail: 'This assembly will be saved to the inherited office' }],
          'assembly'
        );
      }
    }
  }

  onSavePriceList(_: any) {
    // Your child component can still emit; nothing special needed here.
    this.addPriceList = false;
  }

  onCancelPriceList() {
    this.addPriceList = false;
  }

  onSave() {
    if (!this.currentAssembly) return;

    this.loading = true;

    const fid = this.officesService.getFIDByAssemblyType(this.currentAssembly.Type || '');

    // Build idempotent payload of ALL price rows currently visible in UI.
    // IMPORTANT: Keep APID if it exists; omit APID for brand-new rows
    const fullPriceTable: Partial<AssemblyPriceTable>[] =
      (this.assemblyPricesService.assemblyPrices() ?? []).map(p => {
        // keep APID only if present (>0)
        const maybeAPID = p.APID && p.APID > 0 ? { APID: p.APID } : {};
        return {
          ...maybeAPID,
          PlanID: p.PlanID,
          Price: p.Price,
          Type: p.Type,
          FID: fid
        };
      });

    const assembly: Partial<Assembly> = {
      AID: this.currentAssembly.AID,
      Name: this.currentAssembly.Name,
      Type: this.currentAssembly.Type,
      Description: this.currentAssembly.Description,
      CategoryID: this.currentAssembly.CategoryID,
      FID: fid
    };

    const request: AssemblySaveRequest = {
      assembly,
      assemblyPriceTable: fullPriceTable
    };

    console.log('request', request);

    this.assembliesService.saveAssembly(request).subscribe({
      next: (response) => {
        this.loading = false;
        // Update caches and emit for parent listeners
        this.assemblyCacheService.updateAssemblies(response);
        this.save.emit(response);
      },
      error: (err) => {
        console.error('Failed to save assembly:', err);
        this.loading = false;
      }
    });
  }
}
